import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { TrendingUp, TrendingDown, Clock, Target, Zap, ChevronRight } from "lucide-react";

const TradingSignalsWidget = () => {
  const [signals, setSignals] = useState([]);
  const [filter, setFilter] = useState("all");
  const [currentTime, setCurrentTime] = useState(new Date());
  const navigate = useNavigate();

  // Mock data generator for realistic trading signals
  const generateSignals = () => {
    const symbols = [
      "EURUSD",
      "GBPUSD",
      "USDJPY",
      "AUDUSD",
      "USDCAD",
      "BTCUSD",
      "ETHUSD",
      "AAPL",
      "TSLA",
      "GOOGL",
    ];
    const timeframes = ["15m", "30m", "1h", "4h", "1d"];
    const strategies = [
      "RSI Divergence",
      "MA Cross",
      "Support/Resistance",
      "Breakout",
      "Fibonacci",
    ];
    const confidenceLevels = ["High", "Medium", "Low"];

    return symbols.slice(0, 6).map((symbol, index) => ({
      id: index + 1,
      symbol,
      direction: Math.random() > 0.5 ? "buy" : "sell",
      entry: (Math.random() * 100 + 50).toFixed(4),
      stopLoss: (Math.random() * 100 + 30).toFixed(4),
      takeProfit: (Math.random() * 100 + 80).toFixed(4),
      timeframe: timeframes[Math.floor(Math.random() * timeframes.length)],
      strategy: strategies[Math.floor(Math.random() * strategies.length)],
      confidence:
        confidenceLevels[Math.floor(Math.random() * confidenceLevels.length)],
      timestamp: new Date(Date.now() - Math.random() * 3600000).toISOString(),
      pnl: ((Math.random() - 0.5) * 200).toFixed(2),
      isActive: Math.random() > 0.3,
    }));
  };

  useEffect(() => {
    setSignals(generateSignals());

    // Update time every second
    const timeInterval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    // Simulate real-time signal updates
    const signalInterval = setInterval(() => {
      setSignals((prev) => {
        const updated = [...prev];
        const randomIndex = Math.floor(Math.random() * updated.length);
        updated[randomIndex] = {
          ...updated[randomIndex],
          pnl: ((Math.random() - 0.5) * 200).toFixed(2),
          timestamp: new Date().toISOString(),
        };
        return updated;
      });
    }, 5000);

    return () => {
      clearInterval(timeInterval);
      clearInterval(signalInterval);
    };
  }, []);

  const getTimeAgo = (timestamp) => {
    const diff = new Date() - new Date(timestamp);
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) return `${hours}h ago`;
    if (minutes > 0) return `${minutes}m ago`;
    return "Just now";
  };

  const getConfidenceColor = (confidence) => {
    switch (confidence) {
      case "High":
        return "text-emerald-400 bg-emerald-400/10";
      case "Medium":
        return "text-amber-400 bg-amber-400/10";
      case "Low":
        return "text-red-400 bg-red-400/10";
      default:
        return "text-gray-400 bg-gray-400/10";
    }
  };

  const handleLearnMore = (signal) => {
    navigate(`/signal/${signal.id}`, { state: { signal } });
  };

  const filteredSignals = signals.filter((signal) => {
    if (filter === "all") return true;
    if (filter === "buy") return signal.direction === "buy";
    if (filter === "sell") return signal.direction === "sell";
    if (filter === "active") return signal.isActive;
    return true;
  });

  return (
    <div className="w-full max-w-4xl mx-auto bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl shadow-2xl border border-slate-700/50 overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-slate-700/50 bg-gradient-to-r from-blue-900/20 to-purple-900/20">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-500/20 border border-blue-500/30">
              <Zap className="h-5 w-5 text-blue-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Trading Signals</h2>
              <p className="text-sm text-slate-400">
                Live market opportunities
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-sm text-slate-300">
            <Clock className="h-4 w-4" />
            <span>{currentTime.toLocaleTimeString()}</span>
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="px-6 py-4 border-b border-slate-700/50">
        <div className="flex gap-2">
          {["all", "buy", "sell", "active"].map((tab) => (
            <button
              key={tab}
              onClick={() => setFilter(tab)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                filter === tab
                  ? "bg-blue-500/20 text-blue-400 border border-blue-500/30"
                  : "text-slate-400 hover:text-slate-300 hover:bg-slate-800"
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Signals List */}
      <div className="max-h-96 overflow-y-auto">
        {filteredSignals.map((signal) => (
          <div
            key={signal.id}
            className="px-6 py-4 border-b border-slate-700/30 hover:bg-slate-800/50 transition-colors"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                {/* Direction Indicator */}
                <div
                  className={`p-2 rounded-lg ${
                    signal.direction === "buy"
                      ? "bg-emerald-500/20 border border-emerald-500/30"
                      : "bg-red-500/20 border border-red-500/30"
                  }`}
                >
                  {signal.direction === "buy" ? (
                    <TrendingUp className="h-5 w-5 text-emerald-400" />
                  ) : (
                    <TrendingDown className="h-5 w-5 text-red-400" />
                  )}
                </div>

                {/* Signal Details */}
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1">
                    <span className="text-lg font-bold text-white">
                      {signal.symbol}
                    </span>
                    <span
                      className={`text-sm font-medium ${
                        signal.direction === "buy"
                          ? "text-emerald-400"
                          : "text-red-400"
                      }`}
                    >
                      {signal.direction.toUpperCase()}
                    </span>
                    <span className="text-sm text-slate-400">
                      {signal.timeframe}
                    </span>
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${getConfidenceColor(
                        signal.confidence
                      )}`}
                    >
                      {signal.confidence}
                    </span>
                  </div>

                  <div className="flex items-center gap-4 text-sm text-slate-400">
                    <span className="flex items-center gap-1">
                      <Target className="h-3 w-3" />
                      Entry: {signal.entry}
                    </span>
                    <span>SL: {signal.stopLoss}</span>
                    <span>TP: {signal.takeProfit}</span>
                    <span>{signal.strategy}</span>
                  </div>
                </div>
              </div>

              {/* Status & Performance */}
              <div className="flex flex-col items-end gap-2">
                <div className="flex items-center gap-2">
                  <span
                    className={`text-sm font-medium ${
                      parseFloat(signal.pnl) >= 0 ? "text-emerald-400" : "text-red-400"
                    }`}
                  >
                    {parseFloat(signal.pnl) >= 0 ? "+" : ""}{signal.pnl} pips
                  </span>
                  {signal.isActive && (
                    <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                  )}
                </div>

                {/* Learn More Button */}
                <button
                  onClick={() => handleLearnMore(signal)}
                  className="flex items-center gap-1 px-3 py-1 text-xs font-medium text-blue-400 hover:text-blue-300 bg-blue-500/10 hover:bg-blue-500/20 border border-blue-500/30 hover:border-blue-500/50 rounded-lg transition-all duration-200 group"
                >
                  <span className="hidden sm:inline">Learn More</span>
                  <span className="sm:hidden">Info</span>
                  <ChevronRight className="h-3 w-3 group-hover:translate-x-0.5 transition-transform" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Footer Stats */}
      <div className="px-6 py-4 bg-slate-800/50 border-t border-slate-700/50">
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center">
            <div className="text-lg font-bold text-emerald-400">
              {filteredSignals.filter((s) => s.direction === "buy").length}
            </div>
            <div className="text-xs text-slate-400">Buy Signals</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-bold text-red-400">
              {filteredSignals.filter((s) => s.direction === "sell").length}
            </div>
            <div className="text-xs text-slate-400">Sell Signals</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-bold text-blue-400">
              {filteredSignals.filter((s) => s.isActive).length}
            </div>
            <div className="text-xs text-slate-400">Active</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TradingSignalsWidget;